import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Alert,
  Collapse,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  GaugeChart,
  CircularProgressIndicator,
  MetricInfoIcon,
  RiskBreakdownChart,
} from "../../../../../shared/components/charts";
import {
  DashboardCard,
  CohortGuard,
  MetricBreakdownPanel,
} from "../../../../../shared/components";
import {
  formatTrendPercent,
  getRagColor,
  getMetricDefinition,
} from "../../../../../shared/utils";
import { useMetricsStats } from "../../../../../shared/hooks";
import type { MetricsFilters } from "../../../../../shared/api/types";
import { useWellnessOverviewStats } from "../hooks/useWellnessOverview";
import { roundToDecimal } from "../../shared/utils";

interface WellnessOverviewStatsColumnProps {
  filterParams: MetricsFilters;
  isCentralAdmin: boolean;
  isAnonymised: boolean;
  /** When true, the three cards stack vertically (one comparison column). */
  stacked?: boolean;
  /**
   * When true, each wellness gauge gets an expandable demographic breakdown
   * (department / age / gender / shift) scoped to this column's group.
   */
  showBreakdowns?: boolean;
  /**
   * Controlled expanded-metric state (shared across all comparison columns so
   * they expand the same metric together and stay row-aligned). When omitted,
   * the column manages its own state.
   */
  expandedMetric?: string | null;
  onToggleMetric?: (metricId: string) => void;
}

/**
 * Renders the three stats-derived cards on Wellness Overview — Average HRV gauge,
 * Overall Employee Wellness gauges, and Risk breakdown — for a single filter
 * scope. Safe to render once per comparison group (calls the stats hook once).
 */
export const WellnessOverviewStatsColumn: React.FC<
  WellnessOverviewStatsColumnProps
> = ({
  filterParams,
  isCentralAdmin,
  isAnonymised,
  stacked = false,
  showBreakdowns = false,
  expandedMetric: controlledExpanded,
  onToggleMetric,
}) => {
  const theme = useTheme();
  // Falls back to local state when not driven by a parent (controlled props
  // keep all comparison columns expanding the same metric in lockstep).
  const [localExpanded, setLocalExpanded] = useState<string | null>(null);
  const expandedMetric =
    controlledExpanded !== undefined ? controlledExpanded : localExpanded;
  const toggleMetric = (metricId: string) => {
    if (onToggleMetric) onToggleMetric(metricId);
    else setLocalExpanded((prev) => (prev === metricId ? null : metricId));
  };
  const { data: statsData, isLoading: statsLoading } =
    useWellnessOverviewStats(filterParams);
  // Cohort count for the privacy guard, scoped to this group.
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount =
    cohortQuery.data?.data?.active_users?.value ??
    statsData?.data?.total_active_employees?.value;

  const renderTrendBadge = (change: string | undefined) => {
    const normalized = formatTrendPercent(change) ?? "";
    if (!normalized) return null;
    const isNeg = normalized.startsWith("-");
    const isPos = normalized.startsWith("+");
    const color = isNeg
      ? theme.palette.error.main
      : isPos
      ? theme.palette.success.main
      : theme.palette.text.secondary;
    const Icon = isNeg ? TrendingDownIcon : isPos ? TrendingUpIcon : TrendingFlatIcon;
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color }}>
        <Typography component="span" sx={{ fontWeight: 600, fontSize: "0.8rem", color }}>
          {normalized}
        </Typography>
        <Icon sx={{ fontSize: "0.9rem", color }} />
      </Box>
    );
  };

  const resolveRagColor = (
    metricId: string,
    value: number,
    apiRagColor: string | undefined
  ): string | undefined => {
    if (apiRagColor) return getRagColor(apiRagColor, theme);
    const ragColor = getMetricDefinition(metricId)?.ragForValue?.(value);
    return ragColor ? getRagColor(ragColor, theme) : undefined;
  };

  const renderGaugeStatus = (
    metricId: string,
    ragLabel: string | undefined,
    ragColor: string | undefined,
    targetLine?: string
  ) => (
    <Stack spacing={0.5} alignItems="center">
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        {ragLabel && ragColor && (
          <Chip
            label={ragLabel}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
              color: ragColor,
              backgroundColor: alpha(ragColor, 0.12),
            }}
          />
        )}
        <MetricInfoIcon metricKey={metricId} />
      </Box>
      {targetLine && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
          {targetLine}
        </Typography>
      )}
    </Stack>
  );

  // Expandable demographic breakdown toggle shown under each gauge (average view).
  const renderBreakdownToggle = (metricId: string) => {
    if (!showBreakdowns) return null;
    const open = expandedMetric === metricId;
    return (
      <Box sx={{ pb: 2 }}>
        <Button
          size="small"
          variant="text"
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          }
          onClick={() => toggleMetric(metricId)}
          sx={{ fontSize: "0.7rem", textTransform: "none", color: "text.secondary" }}
        >
          {open ? "Hide breakdown" : "Breakdown"}
        </Button>
      </Box>
    );
  };

  const guardProps = {
    userCount: activeUserCount,
    bypass: isCentralAdmin || !isAnonymised,
    loading: statsLoading,
  };

  // HRV gauge bits
  const hrv = statsData?.data?.average_hrv ?? statsData?.data?.hrv;
  // Null (rather than 0) when the cohort recorded no HRV, so the gauge can show
  // "No data" instead of parking the needle at the bottom of the scale.
  const hrvValue =
    hrv?.value === null || hrv?.value === undefined
      ? null
      : roundToDecimal(hrv.value);
  const hrvBaseline = (statsData?.data?.average_hrv?.additional_fields?.baseline ||
    statsData?.data?.hrv?.additional_fields?.baseline) as
    | { min: number; max: number }
    | undefined;

  // Wellness gauges
  const recoveryStat = statsData?.data?.recovery ?? statsData?.data?.recovery_score;
  const strainStat = statsData?.data?.strain;
  const sleepStat = statsData?.data?.sleep_performance;
  const recoveryValue = roundToDecimal(recoveryStat?.value || 0);
  const strainValue = roundToDecimal(strainStat?.value || 0);
  const sleepValue = roundToDecimal(sleepStat?.value || 0);
  const strainTarget = roundToDecimal(
    (strainStat?.additional_fields?.target as number | undefined) || 15
  );
  const recoveryColor = resolveRagColor("recovery_score", recoveryValue, recoveryStat?.rag_color);
  const strainColor = resolveRagColor("strain", strainValue, strainStat?.rag_color);
  const sleepColor = resolveRagColor("sleep_performance", sleepValue, sleepStat?.rag_color);

  // Risk breakdown
  const totalActive = statsData?.data?.total_active_employees?.value || 0;
  const highRiskCount = Math.round((totalActive * roundToDecimal(statsData?.data?.high_risk_employees?.value || 0)) / 100);
  const mediumRiskCount = Math.round((totalActive * roundToDecimal(statsData?.data?.medium_risk_employees?.value || 0)) / 100);
  const lowRiskCount = Math.round((totalActive * roundToDecimal(statsData?.data?.low_risk_employees?.value || 0)) / 100);

  const stackDirection = stacked
    ? ("column" as const)
    : ({ xs: "column", md: "row" } as const);

  return (
    <Stack spacing={2}>
    <Box
      sx={{
        display: "grid",
        gap: 2,
        // CSS Grid equalises row heights: in row mode all three columns share
        // the tallest card's height; stacked (comparison) mode is a single
        // column. align-items defaults to stretch, and each DashboardCard's
        // height:100% then fills the grid cell.
        gridTemplateColumns: stacked ? "1fr" : { xs: "1fr", md: "repeat(3, 1fr)" },
        alignItems: "stretch",
        minWidth: 0,
      }}
    >
      {/* Average HRV */}
      <DashboardCard
        title={hrv?.label || "Average HRV"}
        subtitle="Gauge shows how current HRV compares to baseline range."
        expandable={false}
      >
        <CohortGuard {...guardProps}>
          <GaugeChart
            value={hrvValue}
            min={0}
            max={120}
            baseline={
              hrvBaseline
                ? { min: roundToDecimal(hrvBaseline.min), max: roundToDecimal(hrvBaseline.max) }
                : undefined
            }
            unit={hrv?.unit || "ms"}
            height={200}
            title={hrv?.label || "Average HRV"}
            useNeutralColors
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1, pb: 2 }}>
            <Box>
              {showBreakdowns && (
                <Button
                  size="small"
                  variant="text"
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transform: expandedMetric === "hrv" ? "rotate(180deg)" : "none",
                        transition: "transform 0.2s",
                      }}
                    />
                  }
                  onClick={() => toggleMetric("hrv")}
                  sx={{ fontSize: "0.7rem", textTransform: "none", color: "text.secondary" }}
                >
                  {expandedMetric === "hrv" ? "Hide breakdown" : "Breakdown"}
                </Button>
              )}
            </Box>
            <Box>{renderTrendBadge(hrv?.change)}</Box>
          </Box>
        </CohortGuard>
      </DashboardCard>

      {/* Overall Employee Wellness */}
      <DashboardCard
        title="Overall Employee Wellness"
        tooltip="Employee wellness metrics overview"
        expandable={false}
      >
        <CohortGuard {...guardProps}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 3,
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <CircularProgressIndicator
                value={recoveryValue}
                label="RECOVERY"
                icon={<PersonIcon fontSize="small" />}
                color={recoveryColor}
                unit={recoveryStat?.unit || "%"}
              />
              {renderTrendBadge(recoveryStat?.change)}
              {renderGaugeStatus("recovery_score", recoveryStat?.rag_label, recoveryColor, "Good ≥ 65%")}
              {renderBreakdownToggle("recovery_score")}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <CircularProgressIndicator
                value={strainValue}
                label="STRAIN"
                icon={<DirectionsRunIcon fontSize="small" />}
                color={strainColor}
                max={roundToDecimal((strainStat?.additional_fields?.max as number | undefined) || 21)}
                showExtraTrack
                extraTrackValue={strainTarget}
                extraTrackColor={theme.palette.action.selected}
              />
              {renderTrendBadge(strainStat?.change)}
              {renderGaugeStatus("strain", strainStat?.rag_label, strainColor, `Target ≤ ${strainTarget}`)}
              {renderBreakdownToggle("strain")}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <CircularProgressIndicator
                value={sleepValue}
                label="SLEEP"
                icon={<BedtimeIcon fontSize="small" />}
                color={sleepColor}
                unit={sleepStat?.unit || "%"}
              />
              {renderTrendBadge(sleepStat?.change)}
              {renderGaugeStatus("sleep_performance", sleepStat?.rag_label, sleepColor, "Good ≥ 75%")}
              {renderBreakdownToggle("sleep_performance")}
            </Box>
          </Box>
        </CohortGuard>
      </DashboardCard>

      {/* Risk breakdown */}
      <DashboardCard
        title="Risk breakdown"
        subtitle="Counts of active employees by risk band and participation rate."
        tooltip="Risk score is calculated by averaging four banded risk components (Recovery, Sleep, Strain, HRV), then mapped to Low/Medium/High."
        expandable={false}
      >
        <CohortGuard {...guardProps}>
          {totalActive > 0 ? (
            <RiskBreakdownChart
              data={{ high: highRiskCount, medium: mediumRiskCount, low: lowRiskCount }}
              total={totalActive}
              participationRate={roundToDecimal(
                statsData?.data?.wellness_participation_rate?.value ||
                  statsData?.data?.participation_rate?.value ||
                  0
              )}
              title={statsData?.data?.total_active_employees?.label || "Total Active Employees"}
            />
          ) : (
            <Alert severity="info">No risk breakdown data available.</Alert>
          )}
        </CohortGuard>
      </DashboardCard>
    </Box>

      {/* Full-width demographic breakdown — lives below the 3-card row so it has
          room to breathe and never distorts the cards' equal heights. */}
      {showBreakdowns && (
        <Collapse in={Boolean(expandedMetric)} unmountOnExit>
          {expandedMetric && (
            <DashboardCard
              title={`${
                getMetricDefinition(expandedMetric)?.plainName ?? expandedMetric
              } breakdown`}
              subtitle="By department, age, gender and shift for the current view."
              expandable={false}
              titleComponent={
                <Button
                  size="small"
                  variant="text"
                  startIcon={<CloseIcon sx={{ fontSize: "1rem" }} />}
                  onClick={() => toggleMetric(expandedMetric)}
                  sx={{ fontSize: "0.75rem", textTransform: "none", color: "text.secondary" }}
                >
                  Close
                </Button>
              }
            >
              <MetricBreakdownPanel
                metricId={expandedMetric}
                metricLabel={
                  getMetricDefinition(expandedMetric)?.plainName ?? expandedMetric
                }
                unit={expandedMetric === "strain" ? "" : expandedMetric === "hrv" ? "ms" : "%"}
                filterParams={filterParams}
              />
            </DashboardCard>
          )}
        </Collapse>
      )}
    </Stack>
  );
};
